<script>
  import { onMount, onDestroy, tick } from "svelte";
  import Icon from "@iconify/svelte";
  import broadcastIcon from "@iconify/icons-mdi/broadcast";
  import hljs from "highlight.js/lib/core";
  import jsonLang from "highlight.js/lib/languages/json";
  import "highlight.js/styles/github-dark.min.css";
  import { t } from "./lib/i18n.js";

  hljs.registerLanguage("json", jsonLang);

  export let apiBase = "";
  export let apiKey = "";
  export let locale = "en";
  export let connected = false;
  export let paused = false;
  export let setRefresh = () => {};
  export let onUnauthorized = () => {};
  export let onApiError = () => {};

  let latestWebhook = null;
  let pollInterval = null;
  let error = "";
  let bodyCodeEl = null;
  let lastHighlightedId = null;

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  async function fetchLatest() {
    try {
      const res = await fetch(`${apiBase}/latest`, {
        headers: { Accept: "application/json", ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(t(locale, "auth_required"));
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      latestWebhook = data.webhook;
      connected = true;
      error = "";
    } catch (e) {
      error = e.message;
      connected = false;
      onApiError(e.message);
    }
  }

  function startPolling() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = setInterval(() => {
      if (!paused) {
        fetchLatest();
      }
    }, 250);
  }

  function refresh() {
    fetchLatest();
  }

  onMount(() => {
    setRefresh(refresh);
    fetchLatest();
    startPolling();
  });

  onDestroy(() => {
    if (pollInterval) clearInterval(pollInterval);
  });

  function getMethodClass(method) {
    const m = method?.toLowerCase();
    return `method-${m}`;
  }

  function formatTimestamp(ts) {
    return new Date(ts).toLocaleString();
  }

  function isMultipart(headers) {
    const ct = (headers && headers["content-type"]) || "";
    return ct.toLowerCase().includes("multipart/form-data");
  }

  const TRUNCATE_LEN = 80;
  let expandedHeaderKeys = {};
  function toggleHeader(key) {
    expandedHeaderKeys = { ...expandedHeaderKeys, [key]: !expandedHeaderKeys[key] };
  }

  $: if (latestWebhook && latestWebhook.id !== lastHighlightedId) {
    lastHighlightedId = latestWebhook.id;
    (async () => {
      await tick();
      if (!bodyCodeEl) return;
      const raw = latestWebhook.is_json
        ? (() => {
            try {
              return JSON.stringify(JSON.parse(latestWebhook.body), null, 2);
            } catch {
              return latestWebhook.body;
            }
          })()
        : (latestWebhook.body || "(empty)");
      bodyCodeEl.textContent = raw;
      if (latestWebhook.is_json) {
        try {
          delete bodyCodeEl.dataset.highlighted;
          hljs.highlightElement(bodyCodeEl);
        } catch (_) {}
      }
    })();
  }
  $: if (!latestWebhook) lastHighlightedId = null;
</script>

<main class="w-full p-4 sm:p-6">
  <section class="ui-card p-5 sm:p-6">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span class="inline-flex h-9 w-9 items-center justify-center rounded border border-violet-400/60 bg-violet-400/20 text-white">
          <Icon icon={broadcastIcon} width="18" height="18" />
        </span>
        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t(locale, "live_title")}</h1>
          <p class="mt-0.5 text-sm text-zinc-400 hidden sm:block">{t(locale, "live_subtitle")}</p>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-3 sm:justify-end">
        {#if paused}
          <span class="text-xs text-flytrap-neon bg-yellow-400/10 px-2 py-1 rounded">{t(locale, "live_paused")}</span>
        {/if}
        <div class="text-sm text-zinc-400">
          {t(locale, "live_send_to")} <code class="text-zinc-300 bg-white/5 px-2 py-1 rounded font-mono text-xs">{window.location.origin}/your/custom/path</code>
        </div>
      </div>
    </div>

    <div class="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
      <!-- Headers: left, 1/3 -->
      <div class="ui-card-inner p-6 lg:col-span-1">
        {#if !latestWebhook}
          <div class="text-center py-12 text-zinc-500">
            {t(locale, "live_empty")}
          </div>
        {:else}
          <div class="space-y-2 overflow-y-auto">
            {#each Object.entries(latestWebhook.headers || {}) as [key, value]}
              {@const isLong = (value || "").length > TRUNCATE_LEN}
              {@const expanded = expandedHeaderKeys[key]}
              <div class="flex flex-col gap-1 py-2 border-b border-white/5">
                <div class="flex flex-wrap items-baseline gap-2">
                  <span class="text-xs text-zinc-500 font-mono font-medium shrink-0">{key}:</span>
                  {#if isLong && !expanded}
                    <span class="text-xs text-zinc-300 font-mono break-all">{value.slice(0, TRUNCATE_LEN)}…</span>
                    <button type="button" class="text-xs text-zinc-400 hover:text-white underline shrink-0" onclick={() => toggleHeader(key)}>{t(locale, "live_show_more")}</button>
                  {:else}
                    <span class="text-xs text-zinc-300 font-mono break-all">{value}</span>
                    {#if isLong}
                      <button type="button" class="text-xs text-zinc-400 hover:text-white underline shrink-0" onclick={() => toggleHeader(key)}>{t(locale, "live_show_less")}</button>
                    {/if}
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </div>

      <!-- Body: right, 2/3 -->
      <div class="ui-card-inner p-6 lg:col-span-2">
        {#if error}
          <div class="text-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 rounded px-3 py-2">
            {error}
          </div>
        {:else if !latestWebhook}
          <div class="text-center py-12 text-zinc-500">
            <p class="text-lg mb-2">{t(locale, "live_waiting_title")}</p>
            <p class="text-sm">{t(locale, "live_waiting_body")}</p>
          </div>
        {:else}
          <div class="space-y-4">
            <!-- Method and Path -->
            <div class="flex items-center gap-3">
              <span class={`method-badge ${getMethodClass(latestWebhook.method)}`}>
                {latestWebhook.method}
              </span>
              <span class="font-mono text-sm text-white truncate">{latestWebhook.path}</span>
            </div>

            <div class="flex flex-wrap gap-4 text-xs text-zinc-400">
              <span>{t(locale, "live_received", { timestamp: formatTimestamp(latestWebhook.timestamp) })}</span>
              <span>{t(locale, "live_from", { ip: latestWebhook.source_ip })}</span>
              {#if latestWebhook.origin}
                <span>{t(locale, "live_origin")} <span class="text-zinc-300">{latestWebhook.origin}</span></span>
              {/if}
              <span>{t(locale, "live_bytes", { size: latestWebhook.body_size })}</span>
            </div>

            {#if isMultipart(latestWebhook.headers)}
              <div class="rounded border border-sky-400/30 bg-sky-400/10 px-3 py-2 text-sm text-sky-200">
                {t(locale, "live_multipart", { size: latestWebhook.body_size })}
              </div>
            {/if}

            <!-- Payload: no fixed height, page scroll -->
            <div class="overflow-x-auto">
              <pre class="hljs w-full p-4 bg-black/40 border border-white/10 rounded text-sm font-mono text-zinc-300 whitespace-pre-wrap"><code bind:this={bodyCodeEl} class="language-json"></code></pre>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </section>
</main>
